import { useEffect, useMemo, useState } from 'react';
import { Search, Check, Mic2 } from 'lucide-react';
import { GOOGLE_VOICES } from '../services/googleVoiceService';
import { LegacyVoice } from '../types/legacy';
import { resolveBestLegacyVoiceForGoogle, resolveLegacyVoiceCode } from '../services/voiceProfiles';

type VoiceCatalogItem = {
  key: string;
  title: string;
  subtitle: string;
  category: string;
  legacyVoiceId: number;
  legacyVoiceName: string;
};

interface VoiceCatalogPickerProps {
  voices: LegacyVoice[];
  selectedLegacyVoiceId: number | '';
  onSelect: (legacyVoiceId: number) => void;
  className?: string;
}

const normalize = (value: string | null | undefined): string =>
  String(value || '').trim().toLowerCase();

function buildCatalog(voices: LegacyVoice[]): VoiceCatalogItem[] {
  const legacyItems: VoiceCatalogItem[] = voices.map((voice) => {
    const code = resolveLegacyVoiceCode(voice);
    const subtitleParts = [
      code || '',
      voice.gender || '',
      Number.isFinite(Number(voice.pitch)) ? `pitch ${voice.pitch}` : '',
      Number.isFinite(Number(voice.speed)) ? `rate ${voice.speed}` : '',
    ].filter(Boolean);
    return {
      key: `legacy-${voice.id}`,
      title: voice.name || `Voce #${voice.id}`,
      subtitle: subtitleParts.join(' · ') || `ID legacy ${voice.id}`,
      category: 'Legacy',
      legacyVoiceId: voice.id,
      legacyVoiceName: voice.name || `Voce #${voice.id}`,
    };
  });

  const curatedGoogleItems: VoiceCatalogItem[] = GOOGLE_VOICES
    .filter((voice) => voice.language === 'it-IT')
    .slice(0, 16)
    .map((voice) => {
      const mapped = resolveBestLegacyVoiceForGoogle(voice.id, voices, voice.gender);
      if (!mapped) return null;
      return {
        key: `curated-${voice.id}`,
        title: `${voice.name} (${voice.id})`,
        subtitle: `Mappata su: ${mapped.name}`,
        category: 'Consigliata',
        legacyVoiceId: mapped.id,
        legacyVoiceName: mapped.name || `Voce #${mapped.id}`,
      };
    })
    .filter(Boolean) as VoiceCatalogItem[];

  const deduped = new Map<string, VoiceCatalogItem>();
  [...curatedGoogleItems, ...legacyItems].forEach((item) => {
    const key = `${item.legacyVoiceId}-${item.title}`;
    if (!deduped.has(key)) deduped.set(key, item);
  });

  return Array.from(deduped.values());
}

export default function VoiceCatalogPicker({
  voices,
  selectedLegacyVoiceId,
  onSelect,
  className = '',
}: VoiceCatalogPickerProps) {
  const [query, setQuery] = useState('');
  const [selectedItemKey, setSelectedItemKey] = useState<string>('');
  const catalog = useMemo(() => buildCatalog(voices), [voices]);

  useEffect(() => {
    const numericSelectedId = Number(selectedLegacyVoiceId);
    if (!Number.isFinite(numericSelectedId) || numericSelectedId <= 0) {
      setSelectedItemKey('');
      return;
    }
    // Selezione visuale univoca: preferisce la voce legacy pura.
    const preferred =
      catalog.find((item) => item.legacyVoiceId === numericSelectedId && item.category === 'Legacy')
      || catalog.find((item) => item.legacyVoiceId === numericSelectedId)
      || null;
    setSelectedItemKey(preferred?.key || '');
  }, [catalog, selectedLegacyVoiceId]);

  const filtered = useMemo(() => {
    const q = normalize(query);
    if (!q) return catalog;
    return catalog.filter((item) => {
      const haystack = normalize(
        `${item.title} ${item.subtitle} ${item.category} ${item.legacyVoiceName} ${item.legacyVoiceId}`,
      );
      return haystack.includes(q);
    });
  }, [catalog, query]);

  const selectedLabel = useMemo(() => {
    const selected = voices.find((voice) => voice.id === Number(selectedLegacyVoiceId));
    if (!selected) return 'Nessuna voce selezionata';
    const code = resolveLegacyVoiceCode(selected);
    return `${selected.name}${code ? ` (${code})` : ''}`;
  }, [voices, selectedLegacyVoiceId]);

  return (
    <div className={`space-y-3 ${className}`}>
      <div className="rounded-xl border border-primary/20 bg-primary/5 p-3">
        <p className="text-xs text-text-secondary">Voce selezionata</p>
        <p className="text-sm font-semibold text-text-primary">{selectedLabel}</p>
      </div>

      <div className="relative">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary" />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="w-full rounded-xl border border-border pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
          placeholder="Cerca voce per nome, codice, categoria..."
        />
      </div>

      <div className="rounded-xl border border-border bg-white">
        <div className="flex items-center justify-between px-3 py-2 border-b border-border/70">
          <p className="text-xs font-semibold text-text-secondary">Catalogo voci</p>
          <p className="text-xs text-text-secondary">{filtered.length}/{catalog.length}</p>
        </div>
        <div className="max-h-[340px] overflow-y-auto p-2 space-y-2">
          {filtered.length === 0 ? (
            <div className="px-3 py-6 text-center text-sm text-text-secondary">
              Nessuna voce trovata per questa ricerca.
            </div>
          ) : (
            filtered.map((item) => {
              const isSelected = selectedItemKey
                ? selectedItemKey === item.key
                : Number(selectedLegacyVoiceId) === item.legacyVoiceId;
              return (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => {
                    setSelectedItemKey(item.key);
                    onSelect(item.legacyVoiceId);
                  }}
                  className={`w-full text-left rounded-lg border px-3 py-2 transition ${
                    isSelected
                      ? 'border-primary bg-primary/10'
                      : 'border-border hover:border-primary/40 hover:bg-primary/5'
                  }`}
                >
                  <div className="flex items-start gap-2">
                    <div className="mt-0.5">
                      {isSelected ? (
                        <Check size={15} className="text-primary" />
                      ) : (
                        <Mic2 size={15} className="text-text-secondary" />
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-text-primary truncate">{item.title}</p>
                      <p className="text-xs text-text-secondary truncate">{item.subtitle}</p>
                      <p className="text-[11px] text-text-secondary mt-1">
                        {item.category} · Salvataggio legacy: #{item.legacyVoiceId}
                      </p>
                    </div>
                  </div>
                </button>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}

